export function renderGettingStarted(): string {
  return `
    <div class="getting-started-page">
      <div class="container" style="max-width: 720px; padding: 60px 20px;">
        <h1 style="font-size: 2rem; font-weight: 700; color: #111827; margin-bottom: 8px;">Get Started with SideScribe</h1>
        <p style="color: #6b7280; font-size: 1.1rem; margin-bottom: 48px;">SideScribe uses Claude (Anthropic), ChatGPT (OpenAI), and Gemini (Google) to review your documents. You'll need at least one API key to get started. Your keys are stored locally in the Word add-in and are never sent to SideScribe servers.</p>

        <!-- Section 1: Anthropic -->
        <div class="gs-section">
          <div class="gs-section-header">
            <div class="gs-badge gs-badge-claude">Claude</div>
            <h2>Get an Anthropic API Key</h2>
          </div>
          <p class="gs-desc">Required to use Claude Opus, Sonnet, and Haiku models.</p>
          <ol class="gs-steps">
            <li>
              <strong>Create an account</strong> — Go to
              <a href="https://console.anthropic.com" target="_blank" rel="noopener">console.anthropic.com</a>
              and sign up or log in.
            </li>
            <li>
              <strong>Open API Keys</strong> — In the left sidebar, click <em>API Keys</em>.
            </li>
            <li>
              <strong>Create a key</strong> — Click <em>Create Key</em>, give it a name like <code>SideScribe</code>, then click <em>Create API Key</em>.
            </li>
            <li>
              <strong>Copy your key</strong> — Copy the key that begins with <code>sk-ant-</code>. You won't be able to view it again after closing the dialog.
            </li>
            <li>
              <strong>Paste into SideScribe</strong> — Open the Word add-in, click the gear icon in the header, and paste the key into the <em>Anthropic API Key</em> field.
            </li>
          </ol>
          <div class="gs-note">
            <strong>Note:</strong> Anthropic requires a paid account or credits to use the API. New accounts may receive free credits.
          </div>
        </div>

        <!-- Section 2: OpenAI -->
        <div class="gs-section">
          <div class="gs-section-header">
            <div class="gs-badge gs-badge-openai">OpenAI</div>
            <h2>Get an OpenAI API Key</h2>
          </div>
          <p class="gs-desc">Required to use GPT-4.1, GPT-4o, and other OpenAI models.</p>
          <ol class="gs-steps">
            <li>
              <strong>Create an account</strong> — Go to
              <a href="https://platform.openai.com" target="_blank" rel="noopener">platform.openai.com</a>
              and sign up or log in.
            </li>
            <li>
              <strong>Open API Keys</strong> — In the left sidebar, under the <em>Manage</em> section, click <em>API keys</em>.
            </li>
            <li>
              <strong>Create a key</strong> — Click <em>Create new secret key</em>, optionally name it <code>SideScribe</code>, then click <em>Create secret key</em>.
            </li>
            <li>
              <strong>Copy your key</strong> — Copy the key that begins with <code>sk-</code>. Save it now — it won't be shown again.
            </li>
            <li>
              <strong>Paste into SideScribe</strong> — Open the Word add-in, click the gear icon in the header, and paste the key into the <em>OpenAI API Key</em> field.
            </li>
          </ol>
          <div class="gs-note">
            <strong>Note:</strong> OpenAI requires a paid account or credits. New accounts may receive free credits to get started.
          </div>
        </div>

        <!-- Section 3: Google Gemini -->
        <div class="gs-section">
          <div class="gs-section-header">
            <div class="gs-badge gs-badge-gemini">Gemini</div>
            <h2>Get a Google AI Studio API Key</h2>
          </div>
          <p class="gs-desc">Required to use Gemini 2.5 Pro, Gemini 2.0 Flash, Gemini 1.5 Pro, and Gemini 1.5 Flash models.</p>
          <ol class="gs-steps">
            <li>
              <strong>Go to AI Studio</strong> — Visit
              <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener">aistudio.google.com/apikey</a>
              and sign in with your Google account.
            </li>
            <li>
              <strong>Create a project</strong> — If prompted, create a new Google Cloud project or select an existing one.
            </li>
            <li>
              <strong>Enable the API</strong> — The Generative Language API will be enabled automatically when you create a key.
            </li>
            <li>
              <strong>Create a key</strong> — Click <em>Create API key</em>. Your key will begin with <code>AIza</code>.
            </li>
            <li>
              <strong>Paste into SideScribe</strong> — Open the Word add-in, click the gear icon in the header, and paste the key into the <em>Google AI Studio API Key</em> field.
            </li>
          </ol>
          <div class="gs-note">
            <strong>Note:</strong> Google AI Studio offers a free tier with generous usage limits. Check <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener">AI Studio</a> for current rate limits and pricing.
          </div>
        </div>

        <!-- Privacy note -->
        <div class="gs-privacy">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <div>
            <strong>Your keys stay private.</strong> API keys are stored locally in the Word add-in on your device and sent directly to Anthropic or OpenAI when you run a review. SideScribe servers never receive or store your API keys.
          </div>
        </div>

        <div style="margin-top: 40px;">
          <a href="#/dashboard" class="btn btn-primary">Go to Dashboard</a>
        </div>
      </div>
    </div>
  `;
}

export function mountGettingStarted(): void {
  // No dynamic behavior needed
}
